import { useCallback, useEffect, useRef, useState } from 'react';
/** Loads data from the API with loading and error state; reload() fetches again. */
export function useLoad(load, deps = []) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const latest = useRef(load);
    latest.current = load;
    const reload = useCallback(async () => {
        setLoading(true);
        try {
            setData(await latest.current());
            setError(null);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Could not load the data');
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        void reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
    return { data, error, loading, reload };
}
const DATE = new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
const DATE_TIME = new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
/** "21 Oct 2026" from an ISO date or date-time. */
export function formatDate(value) {
    if (!value)
        return '—';
    const date = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
    return Number.isNaN(date.getTime()) ? value : DATE.format(date);
}
export function formatDateTime(value) {
    if (!value)
        return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : DATE_TIME.format(date);
}
/** "3 min ago", "2 days ago", or the date once it is over a month old. */
export function timeAgo(value) {
    if (!value)
        return '—';
    const seconds = Math.round((Date.now() - new Date(value).getTime()) / 1000);
    if (Number.isNaN(seconds))
        return value;
    if (seconds < 60)
        return 'just now';
    const minutes = Math.round(seconds / 60);
    if (minutes < 60)
        return `${minutes} min ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24)
        return `${hours} h ago`;
    const days = Math.round(hours / 24);
    if (days < 30)
        return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    return formatDate(value);
}
/** A filter tab's label with its count, or the bare label while the first load is running (no false zeros). */
export function countLabel(label, count, ready) {
    return ready ? `${label} ${count}` : label;
}
const RUPEES = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 });
const RUPEES_EXACT = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** "₹9,999" from paise, with Indian grouping (₹1,00,000); paise shown only when there are any. */
export function formatRupees(paise) {
    return paise % 100 === 0 ? RUPEES.format(paise / 100) : RUPEES_EXACT.format(paise / 100);
}
/** "₹8,473.73" from paise, always with two decimals (invoices). */
export function formatRupeesExact(paise) {
    return RUPEES_EXACT.format(paise / 100);
}
/** An Indian mobile number typed any way (98765 43210, +91 98765-43210). The API keeps the 10 digits. */
export const MOBILE_RULES = [{ required: true, pattern: /^(\+?91[\s-]*)?[6-9](\s*-?\d){9}$/, message: 'Enter a 10-digit mobile number.' }];
/** "+91 98765 43210" from 10 digits or a number already shown that way; '' when there is none. */
export function mobileLabel(mobile) {
    if (!mobile)
        return '';
    const digits = mobile.replace(/\D/g, '').slice(-10);
    return digits.length === 10 ? `+91 ${digits.slice(0, 5)} ${digits.slice(5)}` : mobile;
}
export function formatBytes(bytes) {
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${Math.round(bytes / 1024)} KB`;
    if (bytes < 1024 * 1024 * 1024)
        return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
export function initials(name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() || '?';
}
/** A steady colour per person, so the same account always gets the same avatar. */
export function avatarColor(seed) {
    const palette = ['#4f46e5', '#0e9384', '#dd2590', '#e04f16', '#1570ef', '#7839ee', '#099250', '#c4320a'];
    let hash = 0;
    for (const char of seed)
        hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
    return palette[hash % palette.length];
}
export function todayIso() {
    return new Date().toISOString().slice(0, 10);
}
export function daysBetween(fromIso, toIso) {
    return Math.round((new Date(`${toIso}T00:00:00Z`).getTime() - new Date(`${fromIso}T00:00:00Z`).getTime()) / 86400000);
}
/** Where a subscription stands today; "expiring" is the last 30 days. */
export function subscriptionState(row, today = todayIso()) {
    if (row.validFrom > today)
        return 'scheduled';
    if (row.validUntil >= today)
        return daysBetween(today, row.validUntil) <= 30 ? 'expiring' : 'active';
    return daysBetween(row.validUntil, today) <= row.graceDays ? 'grace' : 'expired';
}
/** "account.create" → "Account created", for the activity feed and audit log. */
export function actionLabel(action) {
    const labels = {
        'account.create': 'Account created',
        'account.disable': 'Account disabled',
        'account.enable': 'Account enabled',
        'account.profile': 'Account details changed',
        'licence.issue': 'Subscription issued',
        // Activation keys are gone; these stay for older rows.
        'licence.key_reset': 'New activation key',
        'licence.key_view': 'Activation key viewed',
        'plan.create': 'Plan created',
        'plan.update': 'Plan changed',
        'payment.order': 'Checkout started',
        'payment.paid': 'Payment received',
        'payment.failed': 'Payment failed',
        'settings.update': 'Settings changed',
        'admin.create': 'Admin added',
        'admin.enable': 'Admin enabled',
        'admin.disable': 'Admin disabled',
        'admin.mobile': 'Admin mobile changed',
        'device.activate': 'Computer activated',
        'device.sign_in': 'Computer signed in',
        'device.sign_out': 'Computer signed out',
        'device.remove': 'Computer removed',
        'notification.create': 'Update published',
        'notification.delete': 'Update removed',
        'storage.update': 'Cloud storage changed',
        'sync.upload': 'Company uploaded',
        'sync.remove': 'Company removed from cloud',
        'admin.activation': 'Activated by Kivonex',
        'admin.renewal': 'Renewed by Kivonex',
        'dealer.on': 'Made a dealer',
        'dealer.off': 'Dealer turned off',
        'dealer.credit': 'Dealer credits added',
        'dealer.debit': 'Dealer credits taken back',
        'dealer.activation': 'Activated by a dealer',
        'dealer.renewal': 'Renewed by a dealer'
    };
    return labels[action] ?? action.replace(/[._]/g, ' ').replace(/^\w/, (char) => char.toUpperCase());
}
